import { CircularProgress, makeStyles } from "@material-ui/core";
import useVehicleDetails from "../../hooks/vehicle/useVehicleDetails";
import { COLORS } from "../../assets/theme";
import CustomAccordionDetailVehicle from "../atoms/CustomAccordionDetailVehicle";
import useTableStyles from "../../hooks/styles/useTableStyles";
import { FiCheckCircle } from "react-icons/fi";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { adjustDateByTime } from "../../assets/usefulFunctions";
import { QrCodeInvalid } from "../../assets/images";
import FloatingBottomButton from "../atoms/FloatingBottomButton";
import CookieMonster from "../../tools/CookieMonster";
import useRoleUser from "../../hooks/auth/useRoleUser";
import { useNavigate } from "react-router-dom";
import { combinedRoutes } from "../../config/nav";
import useButtonStyles from "../../hooks/styles/useButtonStyles";

type Props = {
  vehicleId: string | undefined;
  isShowFloatingButton: boolean;
  isShowLastSafetyCheckButton: boolean;
};
const useStyles = makeStyles({
  invalidContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    minHeight: "65vh",
    "& h4": {
      color: COLORS.red_1,
      fontWeight: 600,
    },
    "& p": {
      color: COLORS.black_1,
      fontWeight: 600,
    },
  },
  table: {
    marginTop: 0,
    "& tbody > tr > td": {
      fontSize: 12,
    },
  },
  tableInspection: {
    marginTop: 0,
    "& tbody > tr > td": {
      fontSize: 12,
      color: COLORS.white,
      "&:first-child": {
        width: 150,
      },
    },
    borderSpacing: 0,
  },
  statusDetail: {
    padding: 16,
    "&.active": {
      background: COLORS.gradientGreen_5,
      "& div": {
        color: COLORS.white,
      },
    },
    "&.inactive": {
      background: COLORS.gradientRed_2,
      "& div": {
        color: COLORS.white,
      },
    },
    "& div": {
      display: "flex",
      alignItems: "center",
      gap: 4,
      "&.active": { color: COLORS.blue_1 },
      "&.inactive": { color: COLORS.gray_2 },
      "& h5": {
        fontSize: 12,
        fontWeight: "bold",
      },
    },
  },
});

const DetailVehicleComponent = ({
  vehicleId,
  isShowFloatingButton,
  isShowLastSafetyCheckButton = false,
}: Props) => {
  const tableClasses = useTableStyles();
  const classes = useStyles();
  const [
    {
      detailVehicleMap,
      detailOwnedMap,
      detailSpecMap,
      detailRentMap,
      detailUserMap,
    },
    status,
    dataVehicle,
    loading,
  ] = useVehicleDetails(vehicleId);
  const cookie = new CookieMonster();
  const deptId = cookie.loadCookie("deptId");
  const role = useRoleUser();
  const navigate = useNavigate();
  const btn_classes = useButtonStyles();
  /**
   * @description if data exist and dept user logged and dept id is match
   * and role is FSO and props isShowFLoatingButton true then `TRUE`
   */
  const isFloatingButtonPopup: boolean =
    dataVehicle &&
    deptId &&
    dataVehicle.id_dept === deptId &&
    role === "FSO" &&
    !!isShowFloatingButton;
  /**
   * @description if status is inactive and last_inspection_date exist then `TRUE`
   */
  const isInactiveAndLastInspectionExist =
    dataVehicle &&
    role === "FSO" &&
    dataVehicle.status?.toLowerCase() === "inactive" &&
    !!dataVehicle.last_inspection_date;

  return (
    <>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
          <CircularProgress
            size={24}
            color="inherit"
            style={{ color: COLORS.blue_1 }}
          />
        </div>
      ) : dataVehicle ? (
        <div>
          <div style={{ paddingBottom: "1rem" }}>
            <CustomAccordionDetailVehicle
              id="detail-vehicle"
              headerText="ID Kendaraan"
              defaultExpanded
            >
              <table className={[tableClasses.table, classes.table].join(" ")}>
                <colgroup>
                  <col />
                  <col />
                  <col />
                </colgroup>
                <tbody>
                  {detailVehicleMap.map((item, index) => (
                    <tr key={index}>
                      <td>{item.label}</td>
                      <td>:</td>
                      <td>{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CustomAccordionDetailVehicle>
            <CustomAccordionDetailVehicle
              id="detail-spec"
              headerText="Spesifikasi"
            >
              <table className={[tableClasses.table, classes.table].join(" ")}>
                <colgroup>
                  <col />
                  <col />
                  <col />
                </colgroup>
                <tbody>
                  {detailSpecMap.map((item, index) => (
                    <tr key={index}>
                      <td>{item.label}</td>
                      <td>:</td>
                      <td>{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CustomAccordionDetailVehicle>
            {dataVehicle?.owned ? (
              <CustomAccordionDetailVehicle
                id="detail-owned"
                headerText="Owned"
              >
                <table
                  className={[tableClasses.table, classes.table].join(" ")}
                >
                  <colgroup>
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <tbody>
                    {detailOwnedMap.map((item, index) => (
                      <tr key={index}>
                        <td>{item.label}</td>
                        <td>:</td>
                        <td>{item.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CustomAccordionDetailVehicle>
            ) : (
              <CustomAccordionDetailVehicle id="detail-rent" headerText="Rent">
                <table
                  className={[tableClasses.table, classes.table].join(" ")}
                >
                  <colgroup>
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <tbody>
                    {detailRentMap.map((item, index) => (
                      <tr key={index}>
                        <td>{item.label}</td>
                        <td>:</td>
                        <td>{item.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CustomAccordionDetailVehicle>
            )}
            <CustomAccordionDetailVehicle id="detail-user" headerText="User">
              <table className={[tableClasses.table, classes.table].join(" ")}>
                <colgroup>
                  <col />
                  <col />
                  <col />
                </colgroup>
                <tbody>
                  {detailUserMap.map((item, index) => (
                    <tr key={index}>
                      <td>{item.label}</td>
                      <td>:</td>
                      <td>{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CustomAccordionDetailVehicle>
            <div className={`${classes.statusDetail} ${status}`}>
              <div style={{ marginBottom: 8 }}>
                <h5>STATUS: {status.toUpperCase()}</h5>
                {status === "active" ? (
                  <FiCheckCircle
                    color={COLORS.white}
                    size={16}
                    style={{ marginTop: -6 }}
                  />
                ) : (
                  <IoIosCloseCircleOutline
                    size={16}
                    color={COLORS.white}
                    style={{ marginTop: -6 }}
                  />
                )}
              </div>
              <table
                className={[tableClasses.table, classes.tableInspection].join(
                  " "
                )}
              >
                <colgroup>
                  <col />
                  <col />
                  <col />
                </colgroup>
                <tbody>
                  <tr>
                    <td>Terakhir Inspeksi</td>
                    <td>:</td>
                    <td style={{ paddingLeft: 4 }}>
                      {" "}
                      {dataVehicle && dataVehicle.last_inspection_date
                        ? adjustDateByTime(
                            dataVehicle.last_inspection_date,
                            "dd MMMM yyyy"
                          )
                        : "-"}
                    </td>
                  </tr>
                  {
                    <tr>
                      <td>Checklist berlaku s/d</td>
                      <td>:</td>
                      <td style={{ paddingLeft: 4 }}>
                        {dataVehicle && dataVehicle.expiration_inspect_date
                          ? adjustDateByTime(
                              dataVehicle.expiration_inspect_date,
                              "dd MMMM yyyy"
                            )
                          : "-"}
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            {isInactiveAndLastInspectionExist && isShowLastSafetyCheckButton ? (
              <div
                style={{
                  display: "inline-grid",
                  justifyItems: "center",
                  width: "100%",
                }}
              >
                <button
                  className={`${btn_classes.button} outline`}
                  style={{ margin: 16, width: "95%" }}
                  onClick={() =>
                    navigate(
                      `${combinedRoutes.safety_check_inactive}/${vehicleId}`
                    )
                  }
                >
                  <span>LIHAT SAFETY CHECKLIST TERAKHIR</span>
                </button>
              </div>
            ) : null}
          </div>
          {isFloatingButtonPopup ? (
            <FloatingBottomButton
              variant={"secondary"}
              onClick={() =>
                navigate(`${combinedRoutes.vehicle_checklist}/${vehicleId}`)
              }
            >
              ISI DAFTAR SAFETY CHECK
            </FloatingBottomButton>
          ) : null}
        </div>
      ) : (
        <div className={classes.invalidContainer}>
          <img
            src={QrCodeInvalid}
            alt="qr-code-invalid"
            style={{
              width: 200,
            }}
          />
          <h4>ERROR</h4>
          <p>Kode QR kendaraan tidak valid. Silahkan pindai kembali.</p>
        </div>
      )}
    </>
  );
};

export default DetailVehicleComponent;
